import type { PaymentSystem } from '../types/payment';
import type {
  DashboardDuePaymentRow,
  DashboardDuePaymentsSnapshot,
  DashboardDueWindow,
} from '../types/dashboard';
import { formatDate } from './formatting';

const DUE_WINDOWS: readonly DashboardDueWindow[] = ['yesterday', 'today', 'tomorrow', 'other'];
const NAMED_DUE_WINDOWS = ['yesterday', 'today', 'tomorrow'] as const;

const WINDOW_LABELS: Record<(typeof NAMED_DUE_WINDOWS)[number], string> = {
  yesterday: 'Ontem',
  today: 'Hoje',
  tomorrow: 'Amanhã',
};

const toRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
);

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNullableString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  return null;
};

const toDueWindow = (value: unknown): DashboardDueWindow | null => (
  DUE_WINDOWS.find((window) => window === value) ?? null
);

const mapPaymentRow = (raw: unknown): DashboardDuePaymentRow | null => {
  const row = toRecord(raw);
  const studentName = typeof row.studentName === 'string' ? row.studentName : '';
  const dueWindow = toDueWindow(row.dueWindow);
  if (!studentName || !dueWindow) {
    return null;
  }

  return {
    paymentHistoryId: toNumber(row.paymentHistoryId),
    studentId: toNumber(row.studentId),
    studentName,
    coachName: typeof row.coachName === 'string' ? row.coachName : 'Sem professora',
    paymentSystem: toNullableString(row.paymentSystem),
    status: typeof row.status === 'string' ? row.status : '',
    dueDate: toNullableString(row.dueDate),
    value: toNullableNumber(row.value),
    paymentMethod: toNullableString(row.paymentMethod),
    dueWindow,
  };
};

export function mapDashboardDuePayments(raw: unknown): DashboardDuePaymentsSnapshot {
  const payload = toRecord(raw);
  const payments = Array.isArray(payload.payments)
    ? payload.payments
      .map(mapPaymentRow)
      .filter((item): item is DashboardDuePaymentRow => item != null)
    : [];

  return {
    generatedAt: typeof payload.generatedAt === 'string' ? payload.generatedAt : new Date().toISOString(),
    payments,
  };
}

export function filterDuePayments(
  payments: readonly DashboardDuePaymentRow[],
  windows: readonly DashboardDueWindow[],
  gateways: readonly string[],
): DashboardDuePaymentRow[] {
  return payments.filter((payment) => {
    if (windows.length > 0 && !windows.includes(payment.dueWindow)) {
      return false;
    }

    if (gateways.length > 0 && !gateways.includes(payment.paymentSystem ?? '')) {
      return false;
    }

    return true;
  });
}

export function countDuePaymentsByWindow(
  payments: readonly DashboardDuePaymentRow[],
): Record<(typeof NAMED_DUE_WINDOWS)[number], number> {
  return payments.reduce<Record<(typeof NAMED_DUE_WINDOWS)[number], number>>((counts, payment) => {
    if (
      payment.dueWindow === 'yesterday'
      || payment.dueWindow === 'today'
      || payment.dueWindow === 'tomorrow'
    ) {
      counts[payment.dueWindow] += 1;
    }
    return counts;
  }, {
    yesterday: 0,
    today: 0,
    tomorrow: 0,
  });
}

export function duePaymentWindowLabel(
  payment: Pick<DashboardDuePaymentRow, 'dueWindow' | 'dueDate'>,
): string {
  if (
    payment.dueWindow === 'yesterday'
    || payment.dueWindow === 'today'
    || payment.dueWindow === 'tomorrow'
  ) {
    return WINDOW_LABELS[payment.dueWindow];
  }

  return payment.dueDate ? formatDate(payment.dueDate) : '—';
}

export function duePaymentWindowVariant(
  dueWindow: DashboardDueWindow,
): 'danger' | 'warning' | 'info' {
  if (dueWindow === 'yesterday') {
    return 'danger';
  }

  if (dueWindow === 'today') {
    return 'warning';
  }

  return 'info';
}

export const isPaymentSystem = (value: string): value is PaymentSystem => (
  value === 'asaas'
  || value === 'stripe'
  || value === 'paypal'
  || value === 'pagarme'
  || value === 'mercadopago'
  || value === 'pix'
);
